import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from './Navbar';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  MessageSquare, 
  Video, 
  Calendar, 
  Settings,
  UsersRound,
  Sparkles,
  Crown,
  Palmtree,
  Map,
  Shield,
  Briefcase,
  LogOut,
  ShoppingBag,
  BookOpen,
  BarChart3,
  Heart,
  Radio,
  Bookmark,
  FileSignature
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasTier, hasInnerCircleAccess, hasCoachAccess, hasSDCAccess, hasCostaRicaAccess } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Build sidebar links based on user's tiers
  const getSidebarLinks = () => {
    // Admin-specific sidebar (includes Wonder Twin dashboard for Crystal/Dr. Nicole)
    if (user?.role === 'admin') {
      const adminLinks = [
        { href: '/admin', icon: LayoutDashboard, label: 'Admin Dashboard' },
        { href: '/admin/users', icon: Users, label: 'Users' },
        { href: '/admin/coaches', icon: Briefcase, label: 'Coaches' },
        { href: '/admin/group-sessions', icon: Calendar, label: 'Group Sessions' },
        { href: '/admin/requests', icon: Sparkles, label: 'Requests' },
        { href: '/admin/coach-notes', icon: MessageSquare, label: 'Coach Notes' },
        { href: '/admin/invoices', icon: Briefcase, label: 'Invoices' },
        { href: '/admin/content', icon: Radio, label: 'Content' },
        { href: '/admin/communities', icon: UsersRound, label: 'Communities' },
        { href: '/admin/safety', icon: Shield, label: 'Safety' },
        { href: '/admin/esign', icon: FileSignature, label: 'E-Signatures' },
        { href: '/admin/reports', icon: BarChart3, label: 'Reports' },
        { href: '/videos', icon: Video, label: 'The Vault' },
        { href: '/admin/messages', icon: MessageSquare, label: 'Messages' },
        { href: '/settings', icon: Settings, label: 'Settings' }
      ];
      
      // Add Wonder Twin Dashboard for Crystal & Dr. Nicole
      if (user?.is_wonder_twin) {
        adminLinks.splice(1, 0, { href: '/wonder-twin', icon: Crown, label: 'Wonder Twin' });
      }
      
      // Add Technical Support link
      adminLinks.push({ href: '/admin/support', icon: Shield, label: 'Tech Support' });
      
      return adminLinks;
    }

    // Coach-specific sidebar (different from client sidebar)
    if (user?.is_coach) {
      return [
        { href: '/coach', icon: LayoutDashboard, label: 'Dashboard' },
        { href: '/coach/notes', icon: MessageSquare, label: 'Notes' },
        { href: '/coach/invoices', icon: Briefcase, label: 'Invoices' },
        { href: '/coach/agreements', icon: Sparkles, label: 'Agreements' },
        { href: '/coach/messages', icon: MessageSquare, label: 'Messages' },
        { href: '/coach/notifications', icon: UsersRound, label: 'Notifications' },
        { href: '/communities', icon: UsersRound, label: 'Communities' },
        { href: '/settings', icon: Settings, label: 'Settings' }
      ];
    }

    // Regular client sidebar
    const links = [
      { href: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ];

    // Inner Circle+ features
    if (hasInnerCircleAccess()) {
      links.push(
        { href: '/directory', icon: Users, label: 'Member Directory' },
        { href: '/communities', icon: UsersRound, label: 'Communities' },
        { href: '/circles', icon: Heart, label: 'My Circles' },
        { href: '/videos', icon: Video, label: 'The Vault' },
        { href: '/journal', icon: BookOpen, label: 'Journal' },
        { href: '/saved', icon: Bookmark, label: 'Saved Posts' },
        { href: '/messages', icon: MessageSquare, label: 'Messages' },
        { href: '/gift-membership', icon: Heart, label: 'Gift a Membership', highlight: true }
      );
    }

    // Inner Edge features
    if (hasTier('inner_edge')) {
      links.push(
        { href: '/inner-edge', icon: Sparkles, label: 'Inner Edge Hub' },
        { href: '/diagnostic', icon: Sparkles, label: 'Diagnostic' }
      );
    }

    // Costa Rica Retreat
    if (hasCostaRicaAccess()) {
      links.push({ href: '/retreat', icon: Palmtree, label: 'Retreat Hub' });
    }

    // Add YUMMY Marketplace shop link and settings
    links.push(
      { href: 'https://sisterdiamondsmarketplace.com/', icon: ShoppingBag, label: 'YUMMY Marketplace', external: true },
      { href: '/community-guidelines', icon: Shield, label: 'Community Guidelines' },
      { href: '/settings', icon: Settings, label: 'Settings' }
    );

    return links;
  };

  const sidebarLinks = getSidebarLinks();

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-64 flex-col fixed left-0 top-16 bottom-0 bg-white border-r border-sand">
          <div className="flex-1 py-6 px-4 overflow-y-auto">
            <nav className="space-y-1">
              {sidebarLinks.map((link) => {
                // Determine if this link is active
                let isActive;
                if (link.href === '/admin') {
                  // Admin Dashboard should only be active on exact /admin path
                  isActive = location.pathname === '/admin';
                } else if (link.href === '/coach') {
                  // Coach Dashboard should only be active on exact /coach path
                  isActive = location.pathname === '/coach';
                } else if (link.href === '/dashboard') {
                  isActive = location.pathname === '/dashboard';
                } else if (link.external) {
                  isActive = false;
                } else {
                  isActive = location.pathname.startsWith(link.href);
                }
                
                // Handle external links (like YUMMY Marketplace)
                if (link.external) {
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-sand hover:text-forest"
                      data-testid={`sidebar-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <link.icon className="h-5 w-5" />
                      <span className="font-medium">{link.label}</span>
                    </a>
                  );
                }

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-forest text-white' 
                        : link.highlight
                          ? 'text-pink-600 hover:bg-pink-50 hover:text-pink-700 border border-pink-200'
                          : 'text-gray-600 hover:bg-sand hover:text-forest'
                    }`}
                    data-testid={`sidebar-${link.label.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <link.icon className={`h-5 w-5 ${link.highlight && !isActive ? 'text-pink-500' : ''}`} />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* User Info & Logout */}
          {user && (
            <div className="p-4 border-t border-sand">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-forest flex items-center justify-center text-white font-semibold overflow-hidden" data-testid="sidebar-user-avatar">
                  {user.profile_image ? (
                    <img src={user.profile_image} alt={user.full_name} className="h-full w-full object-cover" />
                  ) : (
                    user.full_name?.charAt(0)?.toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{user.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="sidebar-logout"
              >
                <LogOut className="h-4 w-4" />
                <span>Log Out</span>
              </button>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
